import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@shared/ui/button";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useGetChoices } from "../../schedule/hooks";

interface SystemAlertFilters {
  status?: string;
  priority?: string;
  generateDate?: string;
}

interface SystemAlertFilterPanelProps {
  currentFilters: SystemAlertFilters;
  onApplyFilters: (filters: SystemAlertFilters) => void;
  onResetFilters: () => void;
  isLoading?: boolean;
}

export const SystemAlertFilterPanel: React.FC<SystemAlertFilterPanelProps> = ({
  currentFilters = {
    status: "all",
    priority: "all",
    generateDate: "",
  },
  onApplyFilters,
  onResetFilters,
  isLoading = false,
}) => {
  const [tempFilters, setTempFilters] = useState<{
    status: string;
    priority: string;
    generateDate: string;
  }>({
    status: currentFilters?.status || "all",
    priority: currentFilters?.priority || "all",
    generateDate: currentFilters?.generateDate || "",
  });

  const { data: statusList } = useGetChoices("alert_status");
  const { data: priorityList } = useGetChoices("alert_priority");

  // Prepare options for dynamic form
  const statusOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Statuses" }];
    if (statusList?.result) {
      options.push(
        ...statusList.result.map((status: any) => ({
          value: status.key,
          label: status.value,
        }))
      );
    }
    return options;
  }, [statusList]);

  const priorityOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Priorities" }];
    if (priorityList) {
      options.push(
        ...priorityList.map((priority: any) => ({
          value: priority.key,
          label: priority.value,
        }))
      );
    }
    return options;
  }, [priorityList]);

  // Define form fields configuration
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "generateDate",
        type: "datepicker",
        label: "Generate Date",
        placeholder: "Select generate date",
        defaultValue: tempFilters.generateDate,
        fullWidth: true,
        required: false,
      },
      {
        name: "status",
        type: "select",
        label: "Status",
        placeholder: "All Statuses",
        defaultValue: tempFilters.status,
        options: statusOptions,
        fullWidth: true,
        required: false,
      },
      {
        name: "priority",
        type: "select",
        label: "Priority",
        placeholder: "All Priorities",
        defaultValue: tempFilters.priority,
        options: priorityOptions,
        fullWidth: true,
        required: false,
      },
    ],
    [tempFilters, statusOptions, priorityOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    defaultValues: {
      generateDate: tempFilters.generateDate,
      status: tempFilters.status,
      priority: tempFilters.priority,
    },
  });

  // Sync tempFilters when currentFilters change
  useEffect(() => {
    const newTempFilters = {
      status: currentFilters?.status || "all",
      priority: currentFilters?.priority || "all",
      generateDate: currentFilters?.generateDate || "",
    };

    setTempFilters(newTempFilters);
    form.reset(newTempFilters);
  }, [currentFilters, form]);

  // Handle form field changes to update tempFilters
  const handleFieldChange = (fieldName: string, value: any) => {
    setTempFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleApplyFilters = (data: any) => {
    // Update tempFilters with form data
    setTempFilters({
      status: data.status,
      priority: data.priority,
      generateDate: data.generateDate,
    });

    // Apply filters
    const filtersToApply: SystemAlertFilters = {};

    if (data.status && data.status !== "all") {
      filtersToApply.status = data.status;
    }
    if (data.priority && data.priority !== "all") {
      filtersToApply.priority = data.priority;
    }
    if (data.generateDate) {
      filtersToApply.generateDate = data.generateDate;
    }

    onApplyFilters(filtersToApply);
  };

  const handleResetFilters = () => {
    const resetValues = {
      status: "all",
      priority: "all",
      generateDate: "",
    };

    setTempFilters(resetValues);
    form.reset(resetValues);
    onResetFilters();
  };

  // Custom footer with Reset and Apply buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-3 flex justify-end gap-2">
      <Button type="button" variant="outline" onClick={handleResetFilters}>
        Reset Filters
      </Button>
      <Button type="submit">
        {isLoading ? "Applying..." : "Apply Filters"}
      </Button>
    </div>
  );

  return (
    <DynamicForm
      fields={filterFields}
      form={form}
      onSubmit={handleApplyFilters}
      onReset={handleResetFilters}
      onFieldChange={handleFieldChange} // If this prop exists in your DynamicForm
      config={{
        layout: "grid",
        spacing: "medium",
        showSubmitButton: false,
        showResetButton: false,
        classes: {
          fieldContainer: "space-y-2",
          label: "text-sm text-gray-700 font-medium",
          input:
            "rounded-md border px-3 py-2 border-gray-300 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          select:
            "rounded-md border px-3 py-2 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-sm text-red-500 mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      }}
      gridColumns={3}
      responsiveColumns={{
        sm: 1,
        md: 2,
        lg: 3,
      }}
      renderCustomFooter={renderCustomFooter}
    />
  );
};
